export * from './constants';

import { useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { createContext, useContext } from 'use-context-selector';

import {
  initialContextValues,
  PINNING_STEP,
  requiredStepsInPinning,
  VENUE_CONTACT_STEPS,
} from './constants';
import { getNextStep, getPreviousStep, getTitleOfCurrentStep } from './utils';

import { useSearchPlacements } from '~hooks/useSearchPlacements';
import {
  LOCAL_CONTACT_SKIPPED_STEP,
  LOCAL_GOOGLE_PLACE_ID,
  LOCAL_IS_RELOCATION_FLOW,
  LOCAL_PINNING_STEP,
  LOCAL_VENUE_ID,
} from '~providers/VenuePlacementCreationProvider';

const PinningStepContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const PinningStepProvider: React.FC<Props> = ({ children }) => {
  const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>(
    undefined,
  );
  const [selectedGooglePlaceId, setSelectedGooglePlaceId] = useState<
    string | undefined
  >();

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isAddingFromReview, setIsAddingFromReview] = useState<boolean>(false);

  const [isPlacementAssignedStation, setIsPlacementAssignedStation] =
    useState<boolean>(false);

  const [assignedStationId, setAssignedStationId] = useState<
    string | undefined
  >(undefined);
  const [assignedStationImei, setAssignedStationImei] = useState<
    string | undefined
  >(undefined);
  const [assignedStationType, setAssignedStationType] = useState<
    string | undefined
  >(undefined);

  const [isAssigningToExistingPlacement, setIsAssigningToExistingPlacement] =
    useState<boolean>(false);

  const [swapReasonId, setSwapReasonId] = useState<string>('');
  const [relocationReasonId, setRelocationReasonId] = useState<string>('');
  const [swapNote, setSwapNote] = useState<string>('');
  const [relocationNote, setRelocationNote] = useState<string>('');
  const [isRelocationFlow, setIsRelocationFlowState] = useState<boolean>(false);

  const setIsRelocationFlow = useCallback((value: boolean) => {
    localStorage.setItem(LOCAL_IS_RELOCATION_FLOW, String(value));
    setIsRelocationFlowState(value);
  }, []);

  // The contact screen where "Skip for now" was pressed; set means the venue contact is skipped
  const [contactSkippedStep, setContactSkippedStep] = useState<
    PINNING_STEP | undefined
  >(undefined);

  const handleSetContactSkippedStep = useCallback(
    (step: PINNING_STEP | undefined) => {
      if (step) localStorage.setItem(LOCAL_CONTACT_SKIPPED_STEP, step);
      else localStorage.removeItem(LOCAL_CONTACT_SKIPPED_STEP);
      setContactSkippedStep(step);
    },
    [],
  );

  const handleSetSelectedVenueId = useCallback(
    (venueId: string | undefined) => {
      if (venueId) localStorage.setItem(LOCAL_VENUE_ID, venueId);
      else localStorage.removeItem(LOCAL_VENUE_ID);
      setSelectedVenueId(venueId);
    },
    [],
  );

  const handleSetSelectedGooglePlaceId = useCallback(
    (googlePlaceId: string | undefined) => {
      if (googlePlaceId)
        localStorage.setItem(LOCAL_GOOGLE_PLACE_ID, googlePlaceId);
      else localStorage.removeItem(LOCAL_GOOGLE_PLACE_ID);
      setSelectedGooglePlaceId(googlePlaceId);
    },
    [],
  );

  const [selectedPlacementId, setSelectedPlacementId] = useState<
    string | undefined
  >(undefined);

  const handleSetSelectedPlacementId = useCallback(
    (placementId: string | undefined) => {
      setSelectedPlacementId(placementId);
    },
    [],
  );

  const {
    placements,
    onSearchPlacement,
    isLoadingPlacements,
    isHavingExistingPlacement,
  } = useSearchPlacements({
    venueId: selectedVenueId,
  });

  const [pinningStep, setPinningStep] = useState(PINNING_STEP.START);

  const handleSetPinningStep = useCallback((step: PINNING_STEP) => {
    setPinningStep(step);
    localStorage.setItem(LOCAL_PINNING_STEP, step);
  }, []);

  const goToNextStep = useCallback(() => {
    handleSetPinningStep(
      getNextStep(
        pinningStep,
        isHavingExistingPlacement,
        selectedGooglePlaceId,
        isRelocationFlow,
        isPlacementAssignedStation,
        isAssigningToExistingPlacement,
      ),
    );
  }, [
    handleSetPinningStep,
    isAssigningToExistingPlacement,
    isHavingExistingPlacement,
    isPlacementAssignedStation,
    isRelocationFlow,
    pinningStep,
    selectedGooglePlaceId,
  ]);

  // Skipping any contact screen skips the whole contact
  const skipVenueContact = useCallback(() => {
    handleSetContactSkippedStep(pinningStep);
    handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SCAN_QR);
  }, [handleSetContactSkippedStep, handleSetPinningStep, pinningStep]);

  const goToPreviousStep = useCallback(() => {
    /**
     * When going back to update venue placement information
     * from the review and confirm step, mark the step as updating
     */
    if (pinningStep === PINNING_STEP.REVIEW_CONFIRM) {
      setIsUpdating(true);
    }

    const previousStep = getPreviousStep(
      pinningStep,
      selectedVenueId,
      selectedGooglePlaceId,
      isPlacementAssignedStation,
      isRelocationFlow,
      isAddingFromReview,
      isHavingExistingPlacement,
      isAssigningToExistingPlacement,
      contactSkippedStep,
    );
    handleSetPinningStep(previousStep);
    // Back onto a contact screen resumes the contact, so its fields are validated again
    if (VENUE_CONTACT_STEPS.includes(previousStep)) {
      handleSetContactSkippedStep(undefined);
    }
    if (
      pinningStep === PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT ||
      previousStep === PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT
    ) {
      setIsAddingFromReview(false);
    }
  }, [
    contactSkippedStep,
    handleSetContactSkippedStep,
    handleSetPinningStep,
    isAddingFromReview,
    isAssigningToExistingPlacement,
    isHavingExistingPlacement,
    isPlacementAssignedStation,
    isRelocationFlow,
    pinningStep,
    selectedGooglePlaceId,
    selectedVenueId,
  ]);

  const progress = useMemo(() => {
    // Find the index of the current pinning step in the required steps array
    const currentStepIndex = (() => {
      switch (pinningStep) {
        case PINNING_STEP.CONFIRM_VENUE:
          return requiredStepsInPinning.indexOf(
            PINNING_STEP.CREATE_VENUE_ADD_PHONE_NUM,
          );

        case PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT:
          return (
            requiredStepsInPinning.indexOf(
              PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES,
            ) - 1
          );

        case PINNING_STEP.ASSIGN_STATION_SELECT_ACTION:
          return requiredStepsInPinning.indexOf(
            PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES,
          );

        case PINNING_STEP.CONFIRM_RELOCATION:
          return requiredStepsInPinning.indexOf(
            PINNING_STEP.CONFIRM_SWAP_STATION,
          );

        default:
          return requiredStepsInPinning.indexOf(pinningStep);
      }
    })();

    // Total number of steps to complete
    const totalSteps = requiredStepsInPinning.length;

    // If the step is not found (or if it's not part of the required steps), return 0% progress
    if (currentStepIndex === -1) {
      return null;
    }

    // Calculate the progress as a percentage
    const progressPercentage = ((currentStepIndex + 1) / totalSteps) * 100;

    // Return the percentage as a formatted string, rounded to a whole number
    return `${progressPercentage.toFixed(0)}%`;
  }, [pinningStep]);

  const providerValues = useMemo(
    () => ({
      selectedVenueId,
      handleSetSelectedVenueId,

      selectedGooglePlaceId,
      handleSetSelectedGooglePlaceId,

      selectedPlacementId,
      handleSetSelectedPlacementId,
      existingPlacements: placements,
      onSearchExistingPlacement: onSearchPlacement,
      isLoadingPlacements,
      isHavingExistingPlacement,
      isPlacementAssignedStation,
      setIsPlacementAssignedStation,
      pinningStep,
      handleSetPinningStep,
      goToNextStep,
      goToPreviousStep,
      getTitleOfCurrentStep: () => getTitleOfCurrentStep(pinningStep),
      progress,
      requiredStepsInPinning,
      isUpdating,
      setIsUpdating,
      isAddingFromReview,
      setIsAddingFromReview,
      swapReasonId,
      setSwapReasonId,
      relocationReasonId,
      setRelocationReasonId,
      swapNote,
      setSwapNote,
      relocationNote,
      setRelocationNote,
      isRelocationFlow,
      setIsRelocationFlow,
      isAssigningToExistingPlacement,
      setIsAssigningToExistingPlacement,
      assignedStationImei,
      setAssignedStationImei,
      assignedStationType,
      setAssignedStationType,
      assignedStationId,
      setAssignedStationId,
      contactSkippedStep,
      handleSetContactSkippedStep,
      skipVenueContact,
    }),
    [
      selectedVenueId,
      handleSetSelectedVenueId,
      selectedGooglePlaceId,
      handleSetSelectedGooglePlaceId,
      selectedPlacementId,
      handleSetSelectedPlacementId,
      isLoadingPlacements,
      placements,
      onSearchPlacement,
      isHavingExistingPlacement,
      isPlacementAssignedStation,
      assignedStationType,
      assignedStationImei,
      assignedStationId,
      pinningStep,
      handleSetPinningStep,
      goToNextStep,
      goToPreviousStep,
      progress,
      isUpdating,
      isAddingFromReview,
      swapReasonId,
      relocationReasonId,
      swapNote,
      relocationNote,
      isRelocationFlow,
      setIsRelocationFlow,
      isAssigningToExistingPlacement,
      contactSkippedStep,
      handleSetContactSkippedStep,
      skipVenueContact,
    ],
  );

  useEffectOnce(() => {
    const localSelectedVenueId = localStorage.getItem(LOCAL_VENUE_ID);
    if (localSelectedVenueId) {
      setSelectedVenueId(localSelectedVenueId);
    }
  });

  useEffectOnce(() => {
    const localSelectedGooglePlaceId = localStorage.getItem(
      LOCAL_GOOGLE_PLACE_ID,
    );
    if (localSelectedGooglePlaceId) {
      setSelectedGooglePlaceId(localSelectedGooglePlaceId);
    }
  });

  return (
    <PinningStepContext.Provider value={providerValues}>
      {children}
    </PinningStepContext.Provider>
  );
};

export const usePinningStepContext = () => useContext(PinningStepContext);
